// Wave 94 — Locale-pure UI labels for the /{locale}/notary sub-blocks.
//
// The multilingual notary landings (zh/ja/ko/ru/ar/de/fr/es/pt/vi/id) used to
// render the TH/EN-only labels of NotaryLiveTicker, NotaryAuthorByline,
// NotaryCaseStudies, NotaryPricingTable, NotaryEmbassyWall and
// RelatedNotaryLinks. English chrome on a Japanese/Korean/Arabic page is a
// language-mismatch signal for Google and hurts UX, so every label now has a
// native string. Body copy that only exists in TH/EN (case-study narratives,
// activity ticker items) stays in English and is marked lang="en" in the DOM.
//
// Proper nouns kept untranslated on purpose: Notary Public, Apostille,
// Affidavit, POA, MFA, NYC Legal, THB, ICT, Hague 2025.

export interface NotaryBlockLabels {
  ticker: { last: string; ago: string; openNow: string; closed: string; people: string };
  byline: {
    writtenBy: string; reviewedBy: string; license: string; notaryReg: string;
    updated: string; verifiedAt: string;
  };
  cases: { h2: string; sub: string; problem: string; solution: string; outcome: string; timeline: string };
  pricing: {
    h2: string; sub: string; all: string; popular: string; bundleH: string;
    svc: string; fee: string; time: string; note: string; scroll: string;
    catSignature: string; catAffidavit: string; catCopy: string;
    catCorporate: string; catProperty: string; catIntl: string;
  };
  embassy: {
    title: string; subtitle: string; all: string; apostille: string; consular: string;
    clients: string; path: string; langs: string; trust: string;
    notarizations: string; destinations: string; attorneys: string; cases: string;
  };
  related: {
    h: string; sub: string; topActs: string; nationality: string; coverage: string;
    languages: string; next: string; viewAll: string; coverageHub: string;
    notaryHub: string; apostille: string; mfa: string; embassy: string;
  };
}

const th: NotaryBlockLabels = {
  ticker: {
    last: "ล่าสุดที่ทนายรับรอง",
    ago: "นาทีที่แล้ว",
    openNow: "เปิดบริการ — คิววันนี้ประมาณ",
    closed: "ปิดทำการ — เปิดอีกครั้งวันทำการถัดไป 09:00",
    people: "คน",
  },
  byline: {
    writtenBy: "เขียนและตรวจสอบโดย",
    reviewedBy: "ตรวจรับรองโดย",
    license: "เลขใบอนุญาตทนาย",
    notaryReg: "ทะเบียน Notarial Services Attorney",
    updated: "อัปเดตล่าสุด",
    verifiedAt: "ตรวจสอบความถูกต้องโดยทีมทนาย NYC Legal",
  },
  cases: {
    h2: "เคสจริง — Notary Public ของลูกค้าเรา",
    sub: "เคสล่าสุดจากลูกค้าหลากสัญชาติ — เอกสารเดียวกัน เส้นทางเดียวกัน คุณก็ทำได้",
    problem: "โจทย์", solution: "ทางแก้", outcome: "ผลลัพธ์", timeline: "ระยะเวลา",
  },
  pricing: {
    h2: "ตารางราคา Notary Public ทุกประเภท",
    sub: "ราคาเริ่มต้น โปร่งใส ไม่มีค่าใช้จ่ายซ่อน",
    all: "ทั้งหมด", popular: "ยอดนิยมเท่านั้น",
    bundleH: "แพ็กเกจรวม Notary + Legalization",
    svc: "บริการ", fee: "ค่า Notary", time: "เวลา",
    note: "* ราคาแสดงเป็นบาท (THB) ไม่รวม VAT — ราคาแน่นอนแจ้งหลังตรวจเอกสารฉบับจริง",
    scroll: "เลื่อนดูได้ →",
    catSignature: "ลายมือชื่อ", catAffidavit: "Affidavit / Oath", catCopy: "Certified Copy",
    catCorporate: "บริษัท / POA", catProperty: "อสังหาฯ / มรดก", catIntl: "ใช้ต่างประเทศ",
  },
  embassy: {
    title: "รับรองเอกสารใช้ได้ใน 40+ ประเทศ",
    subtitle: "ทีม Notary ของเราดูแลเอกสารให้ลูกค้าจากกว่า 40 ประเทศปลายทาง — รู้เส้นทาง Apostille / Consular ของทุกประเทศ",
    all: "ทั้งหมด", apostille: "Apostille", consular: "สถานทูต (Consular)",
    clients: "ลูกค้า", path: "เส้นทาง", langs: "ภาษา", trust: "ความน่าเชื่อถือ",
    notarizations: "เอกสาร Notarize", destinations: "ประเทศปลายทาง",
    attorneys: "ทนายในทีม", cases: "เคส",
  },
  related: {
    h: "บริการ Notary Public ที่เกี่ยวข้อง",
    sub: "เชื่อมโยงทุกบริการรับรองทนาย — ตามประเภทเอกสาร สัญชาติ และพื้นที่",
    topActs: "บริการยอดนิยม", nationality: "ตามสัญชาติ", coverage: "พื้นที่ให้บริการ",
    languages: "ภาษาอื่น", next: "มักทำคู่กับ", viewAll: "ดูทั้งหมด",
    coverageHub: "ครอบคลุม 77 จังหวัด", notaryHub: "หน้าหลัก Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "รับรอง กต. (MFA)", embassy: "รับรองสถานทูต",
  },
};

const en: NotaryBlockLabels = {
  ticker: {
    last: "Latest notarization", ago: "min ago",
    openNow: "Open now — today's queue ≈",
    closed: "Closed — reopens next business day at 09:00 ICT",
    people: "people",
  },
  byline: {
    writtenBy: "Written and verified by", reviewedBy: "Reviewed by",
    license: "Lawyers Council License", notaryReg: "Notarial Services Attorney Reg.",
    updated: "Last updated", verifiedAt: "Verified by NYC Legal attorney team",
  },
  cases: {
    h2: "Real cases — Notary Public stories from our clients",
    sub: "Recent cases across nationalities — same documents, same path, same result for you",
    problem: "Problem", solution: "Solution", outcome: "Outcome", timeline: "Timeline",
  },
  pricing: {
    h2: "Notary Public — Full price list",
    sub: "Transparent starting prices — every act, every add-on",
    all: "All", popular: "Popular only", bundleH: "Bundle pricing — Notary + Legalization",
    svc: "Service", fee: "Notary fee", time: "Time",
    note: "* Prices in THB, VAT excluded — exact quote confirmed after document review",
    scroll: "Scroll to see more →",
    catSignature: "Signature", catAffidavit: "Affidavit / Oath", catCopy: "Certified Copy",
    catCorporate: "Corporate / POA", catProperty: "Property / Estate", catIntl: "International",
  },
  embassy: {
    title: "Trusted for 40+ Destination Countries",
    subtitle: "Our Notary team has handled documents for clients heading to 40+ countries — we know the Apostille vs Consular path for each one",
    all: "All", apostille: "Apostille", consular: "Embassy Legalization",
    clients: "clients", path: "Path", langs: "Languages", trust: "Trust & Track Record",
    notarizations: "Notarizations", destinations: "Destinations",
    attorneys: "Notary Attorneys", cases: "cases",
  },
  related: {
    h: "Related Notary Public services",
    sub: "Explore the rest of NYC Legal's notary network — by act, by nationality, and by location.",
    topActs: "Most-requested acts", nationality: "By nationality",
    coverage: "Coverage & locations", languages: "Other languages",
    next: "Often paired with", viewAll: "View all",
    coverageHub: "All 77 provinces", notaryHub: "Notary Public hub",
    apostille: "Apostille (Hague 2025)", mfa: "MFA Legalization", embassy: "Embassy Legalization",
  },
};

const zh: NotaryBlockLabels = {
  ticker: {
    last: "最新公证记录", ago: "分钟前",
    openNow: "现正营业 — 今日排队约",
    closed: "已下班 — 下一个工作日 09:00（ICT）恢复服务",
    people: "位",
  },
  byline: {
    writtenBy: "撰写与核实", reviewedBy: "复核律师",
    license: "律师协会执业证号", notaryReg: "Notarial Services Attorney 注册号",
    updated: "最后更新", verifiedAt: "由 NYC Legal 律师团队核实",
  },
  cases: {
    h2: "真实案例 — 客户的 Notary Public 办理经历",
    sub: "来自不同国籍客户的近期案例 — 同样的文件、同样的流程、同样的结果",
    problem: "问题", solution: "解决方案", outcome: "结果", timeline: "所需时间",
  },
  pricing: {
    h2: "Notary Public 收费一览表",
    sub: "起步价透明公开 — 每项公证、每项附加服务",
    all: "全部", popular: "仅热门项目", bundleH: "套餐价格 — Notary + 认证",
    svc: "服务项目", fee: "公证费", time: "时间",
    note: "* 金额为泰铢（THB），未含增值税 — 确切报价以查验原件后为准",
    scroll: "左右滑动查看 →",
    catSignature: "签名认证", catAffidavit: "Affidavit / 宣誓书", catCopy: "文件副本认证",
    catCorporate: "公司 / POA", catProperty: "不动产 / 遗产", catIntl: "境外使用",
  },
  embassy: {
    title: "文件可用于 40 多个目的地国家",
    subtitle: "我们的 Notary 团队已为前往 40 多个国家的客户办理文件 — 熟悉各国的 Apostille 与领事认证路径",
    all: "全部", apostille: "Apostille", consular: "使馆认证",
    clients: "位客户", path: "认证路径", langs: "语言", trust: "信誉与办理记录",
    notarizations: "公证文件", destinations: "目的地国家",
    attorneys: "团队律师", cases: "案例",
  },
  related: {
    h: "相关 Notary Public 服务",
    sub: "浏览 NYC Legal 公证服务网络 — 按文件类型、国籍与地区查找。",
    topActs: "热门办理项目", nationality: "按国籍", coverage: "服务地区",
    languages: "其他语言", next: "常一并办理", viewAll: "查看全部",
    coverageHub: "覆盖全泰 77 府", notaryHub: "Notary Public 主页",
    apostille: "Apostille（Hague 2025）", mfa: "外交部（MFA）认证", embassy: "使馆认证",
  },
};

const ja: NotaryBlockLabels = {
  ticker: {
    last: "最新の公証実績", ago: "分前",
    openNow: "営業中 — 本日の待ち人数の目安",
    closed: "営業時間外 — 翌営業日 09:00（ICT）に再開",
    people: "名",
  },
  byline: {
    writtenBy: "執筆・確認", reviewedBy: "監修",
    license: "弁護士登録番号", notaryReg: "Notarial Services Attorney 登録番号",
    updated: "最終更新", verifiedAt: "NYC Legal 弁護士チームによる内容確認済み",
  },
  cases: {
    h2: "実例 — お客様の Notary Public 手続き",
    sub: "各国のお客様の最近の事例 — 同じ書類、同じ手順で同じ結果に",
    problem: "課題", solution: "対応", outcome: "結果", timeline: "所要期間",
  },
  pricing: {
    h2: "Notary Public 料金一覧",
    sub: "明朗な基本料金 — 各手続きと追加認証のすべて",
    all: "すべて", popular: "人気のみ", bundleH: "セット料金 — Notary + 認証",
    svc: "サービス", fee: "公証料", time: "所要時間",
    note: "※ 金額はタイバーツ（THB）・VAT 別 — 正式な見積りは原本確認後にご案内します",
    scroll: "横にスクロールできます →",
    catSignature: "署名認証", catAffidavit: "Affidavit / 宣誓供述", catCopy: "原本証明コピー",
    catCorporate: "法人 / POA", catProperty: "不動産 / 相続", catIntl: "海外提出用",
  },
  embassy: {
    title: "40 か国以上での使用実績",
    subtitle: "当事務所の Notary チームは 40 か国以上へ提出する書類を扱ってきました — 各国の Apostille と領事認証の経路を把握しています",
    all: "すべて", apostille: "Apostille", consular: "大使館認証",
    clients: "名のお客様", path: "認証経路", langs: "言語", trust: "信頼と実績",
    notarizations: "公証件数", destinations: "提出先の国",
    attorneys: "在籍弁護士", cases: "件",
  },
  related: {
    h: "関連する Notary Public サービス",
    sub: "NYC Legal の公証サービスを書類の種類・国籍・地域から探せます。",
    topActs: "依頼の多い手続き", nationality: "国籍から探す", coverage: "対応エリア",
    languages: "他の言語", next: "あわせて依頼が多い", viewAll: "すべて見る",
    coverageHub: "タイ全 77 県に対応", notaryHub: "Notary Public トップ",
    apostille: "Apostille（Hague 2025）", mfa: "外務省（MFA）認証", embassy: "大使館認証",
  },
};

const ko: NotaryBlockLabels = {
  ticker: {
    last: "최근 공증 처리", ago: "분 전",
    openNow: "영업 중 — 오늘 대기 인원 약",
    closed: "영업 종료 — 다음 영업일 09:00(ICT)에 다시 엽니다",
    people: "명",
  },
  byline: {
    writtenBy: "작성 및 검수", reviewedBy: "감수",
    license: "변호사 등록번호", notaryReg: "Notarial Services Attorney 등록번호",
    updated: "최종 업데이트", verifiedAt: "NYC Legal 변호사팀 검증 완료",
  },
  cases: {
    h2: "실제 사례 — 고객의 Notary Public 처리 이야기",
    sub: "여러 국적 고객의 최근 사례 — 같은 서류, 같은 절차, 같은 결과",
    problem: "문제", solution: "해결", outcome: "결과", timeline: "소요 기간",
  },
  pricing: {
    h2: "Notary Public 전체 요금표",
    sub: "투명한 기본 요금 — 모든 공증 항목과 추가 인증",
    all: "전체", popular: "인기 항목만", bundleH: "패키지 요금 — Notary + 인증",
    svc: "서비스", fee: "공증 비용", time: "소요 시간",
    note: "* 금액은 태국 바트(THB), VAT 별도 — 정확한 견적은 원본 확인 후 안내됩니다",
    scroll: "옆으로 밀어 보세요 →",
    catSignature: "서명 인증", catAffidavit: "Affidavit / 선서진술", catCopy: "원본대조 사본",
    catCorporate: "법인 / POA", catProperty: "부동산 / 상속", catIntl: "해외 제출용",
  },
  embassy: {
    title: "40개국 이상에서 사용된 서류",
    subtitle: "저희 Notary 팀은 40개국 이상으로 제출되는 서류를 처리해 왔으며, 국가별 Apostille와 영사 인증 경로를 잘 알고 있습니다",
    all: "전체", apostille: "Apostille", consular: "대사관 인증",
    clients: "명 고객", path: "인증 경로", langs: "언어", trust: "신뢰와 실적",
    notarizations: "공증 건수", destinations: "제출 국가",
    attorneys: "소속 변호사", cases: "건",
  },
  related: {
    h: "관련 Notary Public 서비스",
    sub: "NYC Legal 공증 네트워크를 서류 종류·국적·지역별로 살펴보세요.",
    topActs: "많이 찾는 공증", nationality: "국적별", coverage: "서비스 지역",
    languages: "다른 언어", next: "함께 진행하는 경우", viewAll: "전체 보기",
    coverageHub: "태국 77개 주 전역", notaryHub: "Notary Public 메인",
    apostille: "Apostille (Hague 2025)", mfa: "외교부(MFA) 인증", embassy: "대사관 인증",
  },
};

const ru: NotaryBlockLabels = {
  ticker: {
    last: "Последнее заверение", ago: "мин назад",
    openNow: "Открыто — очередь сегодня ≈",
    closed: "Закрыто — откроемся в следующий рабочий день в 09:00 (ICT)",
    people: "чел.",
  },
  byline: {
    writtenBy: "Подготовлено и проверено", reviewedBy: "Проверил",
    license: "Лицензия Совета адвокатов", notaryReg: "Рег. номер Notarial Services Attorney",
    updated: "Обновлено", verifiedAt: "Проверено командой юристов NYC Legal",
  },
  cases: {
    h2: "Реальные кейсы — Notary Public для наших клиентов",
    sub: "Недавние случаи клиентов разных национальностей — те же документы и тот же маршрут",
    problem: "Задача", solution: "Решение", outcome: "Результат", timeline: "Сроки",
  },
  pricing: {
    h2: "Notary Public — полный прайс-лист",
    sub: "Прозрачные базовые тарифы — все услуги и дополнения",
    all: "Все", popular: "Только популярные", bundleH: "Пакеты — Notary + легализация",
    svc: "Услуга", fee: "Сбор нотариуса", time: "Срок",
    note: "* Суммы в батах (THB), без НДС — точная стоимость после проверки оригиналов",
    scroll: "Прокрутите вправо →",
    catSignature: "Подпись", catAffidavit: "Affidavit / присяга", catCopy: "Заверенная копия",
    catCorporate: "Компания / POA", catProperty: "Недвижимость / наследство", catIntl: "Для заграницы",
  },
  embassy: {
    title: "Документы принимают в 40+ странах",
    subtitle: "Наша команда Notary оформляла документы для клиентов в более чем 40 стран — мы знаем маршрут Apostille и консульской легализации для каждой",
    all: "Все", apostille: "Apostille", consular: "Легализация в посольстве",
    clients: "клиентов", path: "Маршрут", langs: "Языки", trust: "Опыт и репутация",
    notarizations: "Заверений", destinations: "Стран назначения",
    attorneys: "Юристов в команде", cases: "кейсов",
  },
  related: {
    h: "Похожие услуги Notary Public",
    sub: "Вся сеть нотариальных услуг NYC Legal — по типу документа, национальности и региону.",
    topActs: "Частые услуги", nationality: "По национальности", coverage: "География услуг",
    languages: "Другие языки", next: "Часто заказывают вместе", viewAll: "Показать всё",
    coverageHub: "Все 77 провинций", notaryHub: "Главная Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Легализация в МИД (MFA)", embassy: "Легализация в посольстве",
  },
};

const ar: NotaryBlockLabels = {
  ticker: {
    last: "أحدث توثيق", ago: "دقيقة مضت",
    openNow: "مفتوح الآن — عدد الانتظار اليوم ≈",
    closed: "مغلق — نفتح في يوم العمل التالي الساعة 09:00 (ICT)",
    people: "أشخاص",
  },
  byline: {
    writtenBy: "إعداد ومراجعة", reviewedBy: "روجع بواسطة",
    license: "رخصة مجلس المحامين", notaryReg: "رقم تسجيل Notarial Services Attorney",
    updated: "آخر تحديث", verifiedAt: "تم التحقق من قبل فريق محامي NYC Legal",
  },
  cases: {
    h2: "حالات حقيقية — تجارب عملائنا مع Notary Public",
    sub: "حالات حديثة لعملاء من جنسيات مختلفة — الوثائق نفسها والمسار نفسه",
    problem: "المشكلة", solution: "الحل", outcome: "النتيجة", timeline: "المدة",
  },
  pricing: {
    h2: "Notary Public — قائمة الأسعار الكاملة",
    sub: "أسعار بداية واضحة — لكل إجراء وكل خدمة إضافية",
    all: "الكل", popular: "الأكثر طلبًا فقط", bundleH: "باقات — Notary + التصديق",
    svc: "الخدمة", fee: "رسوم التوثيق", time: "المدة",
    note: "* المبالغ بالبات التايلاندي (THB) دون ضريبة القيمة المضافة — السعر النهائي بعد فحص المستندات الأصلية",
    scroll: "اسحب للمزيد ←",
    catSignature: "توثيق التوقيع", catAffidavit: "Affidavit / إقرار", catCopy: "نسخة مصدّقة",
    catCorporate: "الشركات / POA", catProperty: "العقارات / الميراث", catIntl: "للاستخدام بالخارج",
  },
  embassy: {
    title: "وثائق مقبولة في أكثر من 40 دولة",
    subtitle: "أنجز فريق Notary لدينا مستندات لعملاء متجهين إلى أكثر من 40 دولة — ونعرف مسار Apostille والتصديق القنصلي لكل منها",
    all: "الكل", apostille: "Apostille", consular: "تصديق السفارة",
    clients: "عميل", path: "المسار", langs: "اللغات", trust: "الثقة والسجل",
    notarizations: "عمليات توثيق", destinations: "دول الوجهة",
    attorneys: "محامون في الفريق", cases: "حالة",
  },
  related: {
    h: "خدمات Notary Public ذات الصلة",
    sub: "تصفح شبكة خدمات التوثيق لدى NYC Legal — حسب نوع المستند والجنسية والمنطقة.",
    topActs: "الإجراءات الأكثر طلبًا", nationality: "حسب الجنسية", coverage: "مناطق الخدمة",
    languages: "لغات أخرى", next: "يُطلب عادةً معها", viewAll: "عرض الكل",
    coverageHub: "جميع المحافظات الـ77", notaryHub: "الصفحة الرئيسية Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "تصديق وزارة الخارجية (MFA)", embassy: "تصديق السفارة",
  },
};

const de: NotaryBlockLabels = {
  ticker: {
    last: "Letzte Beglaubigung", ago: "Min. her",
    openNow: "Jetzt geöffnet — Warteschlange heute ≈",
    closed: "Geschlossen — wieder am nächsten Werktag um 09:00 Uhr (ICT)",
    people: "Personen",
  },
  byline: {
    writtenBy: "Verfasst und geprüft von", reviewedBy: "Geprüft von",
    license: "Zulassung der Anwaltskammer", notaryReg: "Reg.-Nr. Notarial Services Attorney",
    updated: "Zuletzt aktualisiert", verifiedAt: "Geprüft vom Anwaltsteam von NYC Legal",
  },
  cases: {
    h2: "Echte Fälle — Notary Public für unsere Mandanten",
    sub: "Aktuelle Fälle von Mandanten verschiedener Nationalitäten — gleiche Dokumente, gleicher Weg",
    problem: "Ausgangslage", solution: "Lösung", outcome: "Ergebnis", timeline: "Dauer",
  },
  pricing: {
    h2: "Notary Public — vollständige Preisliste",
    sub: "Transparente Startpreise — jede Amtshandlung, jede Zusatzleistung",
    all: "Alle", popular: "Nur beliebte", bundleH: "Paketpreise — Notary + Legalisation",
    svc: "Leistung", fee: "Notargebühr", time: "Dauer",
    note: "* Beträge in Thai-Baht (THB), zzgl. MwSt. — verbindliches Angebot nach Prüfung der Originale",
    scroll: "Zum Scrollen wischen →",
    catSignature: "Unterschrift", catAffidavit: "Affidavit / eidesstattliche Erklärung", catCopy: "Beglaubigte Kopie",
    catCorporate: "Unternehmen / POA", catProperty: "Immobilien / Nachlass", catIntl: "Für das Ausland",
  },
  embassy: {
    title: "Anerkannt in über 40 Zielländern",
    subtitle: "Unser Notary-Team hat Dokumente für Mandanten in über 40 Länder bearbeitet — wir kennen den Apostille- bzw. Konsularweg für jedes Land",
    all: "Alle", apostille: "Apostille", consular: "Botschaftslegalisation",
    clients: "Mandanten", path: "Weg", langs: "Sprachen", trust: "Vertrauen & Erfahrung",
    notarizations: "Beglaubigungen", destinations: "Zielländer",
    attorneys: "Anwälte im Team", cases: "Fälle",
  },
  related: {
    h: "Verwandte Notary-Public-Leistungen",
    sub: "Das gesamte Notariatsnetz von NYC Legal — nach Dokumentart, Nationalität und Ort.",
    topActs: "Häufigste Amtshandlungen", nationality: "Nach Nationalität", coverage: "Standorte & Abdeckung",
    languages: "Weitere Sprachen", next: "Wird oft kombiniert mit", viewAll: "Alle ansehen",
    coverageHub: "Alle 77 Provinzen", notaryHub: "Notary-Public-Übersicht",
    apostille: "Apostille (Hague 2025)", mfa: "Legalisation im Außenministerium (MFA)", embassy: "Botschaftslegalisation",
  },
};

const fr: NotaryBlockLabels = {
  ticker: {
    last: "Dernière certification", ago: "min",
    openNow: "Ouvert — file d'attente aujourd'hui ≈",
    closed: "Fermé — réouverture le prochain jour ouvré à 09h00 (ICT)",
    people: "personnes",
  },
  byline: {
    writtenBy: "Rédigé et vérifié par", reviewedBy: "Relu par",
    license: "Licence du Conseil des avocats", notaryReg: "N° d'enregistrement Notarial Services Attorney",
    updated: "Dernière mise à jour", verifiedAt: "Vérifié par l'équipe d'avocats NYC Legal",
  },
  cases: {
    h2: "Cas réels — Notary Public pour nos clients",
    sub: "Cas récents de clients de toutes nationalités — mêmes documents, même parcours",
    problem: "Problème", solution: "Solution", outcome: "Résultat", timeline: "Délai",
  },
  pricing: {
    h2: "Notary Public — grille tarifaire complète",
    sub: "Tarifs de départ transparents — chaque acte, chaque option",
    all: "Tous", popular: "Populaires uniquement", bundleH: "Forfaits — Notary + légalisation",
    svc: "Prestation", fee: "Frais de notaire", time: "Délai",
    note: "* Montants en bahts (THB), hors TVA — devis exact confirmé après examen des originaux",
    scroll: "Faites défiler →",
    catSignature: "Signature", catAffidavit: "Affidavit / déclaration sous serment", catCopy: "Copie certifiée",
    catCorporate: "Société / POA", catProperty: "Immobilier / succession", catIntl: "Usage à l'étranger",
  },
  embassy: {
    title: "Documents acceptés dans plus de 40 pays",
    subtitle: "Notre équipe Notary a traité des documents destinés à plus de 40 pays — nous connaissons la voie Apostille ou consulaire de chacun",
    all: "Tous", apostille: "Apostille", consular: "Légalisation à l'ambassade",
    clients: "clients", path: "Parcours", langs: "Langues", trust: "Confiance et expérience",
    notarizations: "Actes notariés", destinations: "Pays de destination",
    attorneys: "Avocats dans l'équipe", cases: "dossiers",
  },
  related: {
    h: "Services Notary Public associés",
    sub: "Tout le réseau notarial de NYC Legal — par type d'acte, nationalité et localisation.",
    topActs: "Actes les plus demandés", nationality: "Par nationalité", coverage: "Zones desservies",
    languages: "Autres langues", next: "Souvent associé à", viewAll: "Tout voir",
    coverageHub: "Les 77 provinces", notaryHub: "Accueil Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Légalisation au MFA", embassy: "Légalisation à l'ambassade",
  },
};

const es: NotaryBlockLabels = {
  ticker: {
    last: "Última certificación", ago: "min",
    openNow: "Abierto — cola de hoy ≈",
    closed: "Cerrado — reabrimos el próximo día hábil a las 09:00 (ICT)",
    people: "personas",
  },
  byline: {
    writtenBy: "Redactado y verificado por", reviewedBy: "Revisado por",
    license: "Licencia del Colegio de Abogados", notaryReg: "N.º de registro Notarial Services Attorney",
    updated: "Última actualización", verifiedAt: "Verificado por el equipo legal de NYC Legal",
  },
  cases: {
    h2: "Casos reales — Notary Public de nuestros clientes",
    sub: "Casos recientes de clientes de distintas nacionalidades — mismos documentos, mismo recorrido",
    problem: "Problema", solution: "Solución", outcome: "Resultado", timeline: "Plazo",
  },
  pricing: {
    h2: "Notary Public — lista completa de tarifas",
    sub: "Precios iniciales transparentes — cada acto y cada complemento",
    all: "Todos", popular: "Solo populares", bundleH: "Paquetes — Notary + legalización",
    svc: "Servicio", fee: "Honorario notarial", time: "Plazo",
    note: "* Importes en bahts (THB), sin IVA — presupuesto exacto tras revisar los originales",
    scroll: "Desliza para ver más →",
    catSignature: "Firma", catAffidavit: "Affidavit / declaración jurada", catCopy: "Copia certificada",
    catCorporate: "Empresa / POA", catProperty: "Inmuebles / herencia", catIntl: "Uso en el extranjero",
  },
  embassy: {
    title: "Documentos aceptados en más de 40 países",
    subtitle: "Nuestro equipo Notary ha gestionado documentos con destino a más de 40 países — conocemos la vía Apostille o consular de cada uno",
    all: "Todos", apostille: "Apostille", consular: "Legalización en embajada",
    clients: "clientes", path: "Vía", langs: "Idiomas", trust: "Confianza y trayectoria",
    notarizations: "Actos notariales", destinations: "Países de destino",
    attorneys: "Abogados en el equipo", cases: "casos",
  },
  related: {
    h: "Servicios de Notary Public relacionados",
    sub: "Toda la red notarial de NYC Legal — por tipo de acto, nacionalidad y ubicación.",
    topActs: "Actos más solicitados", nationality: "Por nacionalidad", coverage: "Cobertura y sedes",
    languages: "Otros idiomas", next: "Suele combinarse con", viewAll: "Ver todo",
    coverageHub: "Las 77 provincias", notaryHub: "Inicio Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Legalización en el MFA", embassy: "Legalización en embajada",
  },
};

const pt: NotaryBlockLabels = {
  ticker: {
    last: "Última certificação", ago: "min atrás",
    openNow: "Aberto — fila de hoje ≈",
    closed: "Fechado — reabrimos no próximo dia útil às 09:00 (ICT)",
    people: "pessoas",
  },
  byline: {
    writtenBy: "Redigido e verificado por", reviewedBy: "Revisado por",
    license: "Licença da Ordem dos Advogados", notaryReg: "Registro Notarial Services Attorney",
    updated: "Última atualização", verifiedAt: "Verificado pela equipe jurídica da NYC Legal",
  },
  cases: {
    h2: "Casos reais — Notary Public dos nossos clientes",
    sub: "Casos recentes de clientes de várias nacionalidades — mesmos documentos, mesmo caminho",
    problem: "Problema", solution: "Solução", outcome: "Resultado", timeline: "Prazo",
  },
  pricing: {
    h2: "Notary Public — tabela completa de valores",
    sub: "Valores iniciais transparentes — cada ato e cada adicional",
    all: "Todos", popular: "Somente populares", bundleH: "Pacotes — Notary + legalização",
    svc: "Serviço", fee: "Honorário do notário", time: "Prazo",
    note: "* Valores em bahts (THB), sem impostos — orçamento exato após análise dos originais",
    scroll: "Arraste para ver mais →",
    catSignature: "Assinatura", catAffidavit: "Affidavit / declaração juramentada", catCopy: "Cópia autenticada",
    catCorporate: "Empresa / POA", catProperty: "Imóveis / herança", catIntl: "Uso no exterior",
  },
  embassy: {
    title: "Documentos aceitos em mais de 40 países",
    subtitle: "Nossa equipe Notary já cuidou de documentos destinados a mais de 40 países — conhecemos a via Apostille ou consular de cada um",
    all: "Todos", apostille: "Apostille", consular: "Legalização na embaixada",
    clients: "clientes", path: "Via", langs: "Idiomas", trust: "Confiança e histórico",
    notarizations: "Atos notariais", destinations: "Países de destino",
    attorneys: "Advogados na equipe", cases: "casos",
  },
  related: {
    h: "Serviços de Notary Public relacionados",
    sub: "Toda a rede notarial da NYC Legal — por tipo de ato, nacionalidade e localidade.",
    topActs: "Atos mais solicitados", nationality: "Por nacionalidade", coverage: "Cobertura e locais",
    languages: "Outros idiomas", next: "Costuma vir junto com", viewAll: "Ver tudo",
    coverageHub: "As 77 províncias", notaryHub: "Página inicial Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Legalização no MFA", embassy: "Legalização na embaixada",
  },
};

const vi: NotaryBlockLabels = {
  ticker: {
    last: "Hồ sơ chứng thực gần nhất", ago: "phút trước",
    openNow: "Đang mở cửa — hàng chờ hôm nay ≈",
    closed: "Đã đóng cửa — mở lại vào ngày làm việc kế tiếp lúc 09:00 (ICT)",
    people: "khách",
  },
  byline: {
    writtenBy: "Biên soạn và kiểm chứng bởi", reviewedBy: "Thẩm định bởi",
    license: "Số thẻ luật sư", notaryReg: "Số đăng ký Notarial Services Attorney",
    updated: "Cập nhật lần cuối", verifiedAt: "Đã được đội ngũ luật sư NYC Legal kiểm chứng",
  },
  cases: {
    h2: "Tình huống thực tế — Notary Public của khách hàng",
    sub: "Các hồ sơ gần đây của khách nhiều quốc tịch — cùng loại giấy tờ, cùng quy trình",
    problem: "Vấn đề", solution: "Cách xử lý", outcome: "Kết quả", timeline: "Thời gian",
  },
  pricing: {
    h2: "Notary Public — bảng phí đầy đủ",
    sub: "Mức phí khởi điểm minh bạch — mọi thủ tục và dịch vụ đi kèm",
    all: "Tất cả", popular: "Chỉ mục phổ biến", bundleH: "Gói dịch vụ — Notary + hợp pháp hóa",
    svc: "Dịch vụ", fee: "Phí công chứng", time: "Thời gian",
    note: "* Số tiền tính bằng baht (THB), chưa gồm VAT — báo giá chính xác sau khi kiểm tra bản gốc",
    scroll: "Vuốt để xem thêm →",
    catSignature: "Chứng thực chữ ký", catAffidavit: "Affidavit / bản tuyên thệ", catCopy: "Bản sao chứng thực",
    catCorporate: "Doanh nghiệp / POA", catProperty: "Bất động sản / thừa kế", catIntl: "Dùng ở nước ngoài",
  },
  embassy: {
    title: "Giấy tờ được chấp nhận tại hơn 40 quốc gia",
    subtitle: "Đội ngũ Notary của chúng tôi đã xử lý hồ sơ đi hơn 40 quốc gia — nắm rõ lộ trình Apostille hay hợp pháp hóa lãnh sự của từng nước",
    all: "Tất cả", apostille: "Apostille", consular: "Hợp pháp hóa lãnh sự",
    clients: "khách hàng", path: "Lộ trình", langs: "Ngôn ngữ", trust: "Uy tín và kinh nghiệm",
    notarizations: "Hồ sơ đã chứng thực", destinations: "Quốc gia đến",
    attorneys: "Luật sư trong đội", cases: "hồ sơ",
  },
  related: {
    h: "Dịch vụ Notary Public liên quan",
    sub: "Toàn bộ mạng lưới công chứng của NYC Legal — theo loại giấy tờ, quốc tịch và khu vực.",
    topActs: "Thủ tục được yêu cầu nhiều", nationality: "Theo quốc tịch", coverage: "Khu vực phục vụ",
    languages: "Ngôn ngữ khác", next: "Thường làm kèm", viewAll: "Xem tất cả",
    coverageHub: "Toàn bộ 77 tỉnh", notaryHub: "Trang chính Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Hợp pháp hóa tại MFA", embassy: "Hợp pháp hóa lãnh sự",
  },
};

const id: NotaryBlockLabels = {
  ticker: {
    last: "Legalisasi terbaru", ago: "menit lalu",
    openNow: "Sedang buka — antrean hari ini ≈",
    closed: "Tutup — buka lagi pada hari kerja berikutnya pukul 09.00 (ICT)",
    people: "orang",
  },
  byline: {
    writtenBy: "Ditulis dan diperiksa oleh", reviewedBy: "Ditinjau oleh",
    license: "Lisensi Dewan Advokat", notaryReg: "No. registrasi Notarial Services Attorney",
    updated: "Terakhir diperbarui", verifiedAt: "Diverifikasi oleh tim advokat NYC Legal",
  },
  cases: {
    h2: "Kasus nyata — Notary Public untuk klien kami",
    sub: "Kasus terbaru dari klien berbagai kewarganegaraan — dokumen sama, jalur sama",
    problem: "Masalah", solution: "Solusi", outcome: "Hasil", timeline: "Waktu",
  },
  pricing: {
    h2: "Notary Public — daftar biaya lengkap",
    sub: "Biaya awal yang transparan — setiap layanan dan tambahannya",
    all: "Semua", popular: "Hanya populer", bundleH: "Paket — Notary + legalisasi",
    svc: "Layanan", fee: "Biaya notaris", time: "Waktu",
    note: "* Nominal dalam baht (THB), belum termasuk PPN — penawaran pasti setelah dokumen asli diperiksa",
    scroll: "Geser untuk melihat →",
    catSignature: "Tanda tangan", catAffidavit: "Affidavit / sumpah", catCopy: "Salinan tersertifikasi",
    catCorporate: "Perusahaan / POA", catProperty: "Properti / waris", catIntl: "Untuk luar negeri",
  },
  embassy: {
    title: "Dokumen diterima di 40+ negara tujuan",
    subtitle: "Tim Notary kami telah menangani dokumen untuk klien ke lebih dari 40 negara — kami paham jalur Apostille maupun konsuler tiap negara",
    all: "Semua", apostille: "Apostille", consular: "Legalisasi kedutaan",
    clients: "klien", path: "Jalur", langs: "Bahasa", trust: "Kepercayaan & rekam jejak",
    notarizations: "Dokumen dilegalisasi", destinations: "Negara tujuan",
    attorneys: "Advokat di tim", cases: "kasus",
  },
  related: {
    h: "Layanan Notary Public terkait",
    sub: "Seluruh jaringan notaris NYC Legal — menurut jenis dokumen, kewarganegaraan, dan lokasi.",
    topActs: "Layanan paling dicari", nationality: "Menurut kewarganegaraan", coverage: "Cakupan & lokasi",
    languages: "Bahasa lain", next: "Sering diurus bersamaan", viewAll: "Lihat semua",
    coverageHub: "Seluruh 77 provinsi", notaryHub: "Beranda Notary Public",
    apostille: "Apostille (Hague 2025)", mfa: "Legalisasi MFA", embassy: "Legalisasi kedutaan",
  },
};

export const NOTARY_BLOCK_I18N: Record<string, NotaryBlockLabels> = {
  th, en, zh, ja, ko, ru, ar, de, fr, es, pt, vi, id,
};

/** Resolve labels for any locale code, falling back to English. */
export function notaryBlockLabels(lang?: string): NotaryBlockLabels {
  if (!lang) return th;
  return NOTARY_BLOCK_I18N[lang] ?? NOTARY_BLOCK_I18N[lang.slice(0, 2)] ?? en;
}
